import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../core/services/api.service';
import { PartnersApiService } from './partners.service';
import { Partner } from '../models/partner.model';

// Shapes returned by GET /api/homepage/public (BackEnd controllers/homepageController.js).
export interface HomepagePost {
  _id: string;
  postTitle: string;
  postContent: string;
  postUserID: string;
  anonymous?: boolean;
  author: string;
  authorInfluence: number;
  profilePictureKey: string;
  pictureTransform: number;
  timeStamp: string;
  tags: string[];
  numberOfComments: number;
  upVoteUsers: string[];
}

export interface HomepageJob {
  _id: string;
  title: string;
  company?: { _id: string; name: string; logo?: any; website?: string } | null;
  locationType: 'remote' | 'hybrid' | 'onsite';
  location?: { city?: string; state?: string; country?: string };
  jobType: string;
  experienceLevel?: string;
  publishedAt: string | null;
  createdAt: string;
}

export interface HomepageEvent {
  _id: string;
  title: string;
  airDate: string;
  startTime?: string;
  endTime?: string;
  isOnline: boolean;
  location?: string;
  shortDescription?: string;
  pictureKey?: string;
  eventTags?: string[];
}

export interface HomepageDocument {
  _id: string;
  name: string;
  description?: string;
  type?: string;
  category?: string;
  downloads: number;
  timeStamp: string;
}

export type HomepageSource = 'admin' | 'auto';

export interface HomepageData {
  partners: Partner[];         // priority partners for the logo strip (GET /partners/public)
  discussions: HomepagePost[];
  community: HomepagePost[];
  featuredJob: HomepageJob | null;
  events: HomepageEvent[];
  resources: HomepageDocument[];
  source: Record<'discussions' | 'community' | 'featuredJob' | 'events' | 'resources', HomepageSource>;
  updatedAt: string | null;
}

export const EMPTY_HOMEPAGE: HomepageData = {
  partners: [],
  discussions: [], community: [], featuredJob: null, events: [], resources: [],
  source: { discussions: 'auto', community: 'auto', featuredJob: 'auto', events: 'auto', resources: 'auto' },
  updatedAt: null,
};

@Injectable({ providedIn: 'root' })
export class HomepageApiService {
  constructor(private apiService: ApiService, private partnerService: PartnersApiService) {}

  // The five live sections in one call, plus the priority partners for the
  // logo strip. Never throws: the homepage must render its static parts even
  // when the API is down, so each failure resolves to empty and the section
  // shows its empty state.
  getHomepage(): Observable<HomepageData> {
    const sections$ = this.apiService.get<{ data: Partial<HomepageData> }>('/homepage/public').pipe(
      map((res) => (res && res.data ? res.data : {})),
      catchError(() => of({} as Partial<HomepageData>)),
    );
    const partners$ = this.partnerService.getPublicPartners().pipe(
      // Priority is 1..n for featured partners and 0 otherwise; the public route already sorts featured first.
      map((partners: Partner[]) => (partners || []).filter((p: Partner) => (p.priority || 0) > 0).sort((a: Partner, b: Partner) => a.priority - b.priority)),
      catchError(() => of([] as Partner[])),
    );
    return forkJoin({ sections: sections$, partners: partners$ }).pipe(
      map(({ sections, partners }) => ({ ...EMPTY_HOMEPAGE, ...sections, partners })),
    );
  }
}
